/**
 * Utility functions for ExpandableSection component
 */

/**
 * Determines the base CSS class name based on section type
 * @param {boolean} isSubSection - Whether this is a subsection
 * @returns {string} Base class name
 */
export const getBaseClassName = (isSubSection) => 
  isSubSection ? 'expandable-subsection' : 'expandable-section';

/**
 * Builds the complete container class name with modifiers
 * @param {string} baseClassName - Base class name
 * @param {boolean} expanded - Whether section is expanded
 * @param {boolean} hovered - Whether section is hovered
 * @param {string} additionClass - Additional CSS class
 * @returns {string} Complete class name string
 */
export const buildContainerClassName = (baseClassName, expanded, hovered, additionClass) => {
  const classes = [baseClassName];
  
  if (expanded) {
    classes.push(`${baseClassName}--expanded`);
  }
  
  if (expanded && hovered) {
    classes.push('hovered');
  }
  
  if (additionClass) {
    classes.push(additionClass);
  }
  
  return classes.join(' ');
};

/**
 * Creates arrow rotation style object
 * @param {boolean} expanded - Whether section is expanded
 * @returns {object} Style object for arrow rotation
 */
export const getArrowStyle = (expanded) => ({
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.2s ease',
});

/**
 * Determines if component should be in controlled mode
 * @param {boolean|undefined} controlledExpanded - Controlled expanded prop
 * @returns {boolean} Whether component is controlled
 */
export const isControlledComponent = (controlledExpanded) => 
  controlledExpanded !== undefined;

/**
 * Gets the effective expanded state (controlled or internal)
 * @param {boolean|undefined} controlledExpanded - Controlled expanded prop
 * @param {boolean} internalExpanded - Internal expanded state
 * @returns {boolean} Effective expanded state
 */
export const getEffectiveExpandedState = (controlledExpanded, internalExpanded) => 
  isControlledComponent(controlledExpanded) ? controlledExpanded : internalExpanded;

/**
 * Creates a CSS class name for a specific element
 * @param {string} baseClassName - Base class name
 * @param {string} element - Element name (e.g., 'icon', 'content', 'name')
 * @param {string} modifier - Optional modifier (e.g., 'left', 'right')
 * @returns {string} Complete CSS class name
 */
export const createElementClassName = (baseClassName, element, modifier = '') => {
  const elementClass = `${baseClassName}__${element}`;
  return modifier ? `${elementClass} ${elementClass}--${modifier}` : elementClass;
};

/**
 * Handles event propagation stopping
 * @param {Event} event - The event to handle
 */
export const stopEventPropagation = (event) => {
  if (event) {
    event.stopPropagation();
  }
};



// WEBPACK FOOTER //
// ./src/components/ExpandableSection/utils/expandableUtils.js